// ════════════════════════════════════════════════════════════════════════
//  Welche Lehrgänge lassen sich einer Klasse zuordnen?
//
//  Reine Rechnung, ohne React und ohne Datenbank – dieselben Regeln stehen als
//  Trigger in `class_curricula`. Zwei Stellen mit Absicht: der Trigger ist die
//  Grenze, diese Funktion ist die Oberfläche. Ohne sie böte die Liste Lehrgänge
//  an, deren Zuordnung die Datenbank hinterher ablehnt – und die Lehrkraft
//  erführe von der Regel erst durch eine Fehlermeldung.
//
//  Der VORGÄNGER gehört nicht zu diesen Regeln. Er wird ausschließlich in der
//  Auswahl des Kindes erzwungen, nicht hier: Grundkurs 1 kann in Klasse 3 und
//  Grundkurs 2 in Klasse 4 stattfinden, und die Klasse 4 hat den ersten nie als
//  diese Klasse geführt. `requiresId` bleibt trotzdem im Typ – die Oberfläche
//  nennt den Vorgänger, sie verlangt ihn nur nicht.
// ════════════════════════════════════════════════════════════════════════

export type CourseOption = {
  readonly curriculumId: string;
  readonly slug: string;
  readonly title: string;
  readonly subtitle: string | null;
  readonly position: number;
  /** Der Vorgänger, oder null: dann steht der Lehrgang für sich. */
  readonly requiresId: string | null;
  readonly retiredAt: string | null;
  /** Neueste veröffentlichte Fassung, oder null – dann gibt es nichts zu üben. */
  readonly latestVersionId: string | null;
  readonly latestVersion: number | null;
};

/** Was die Klasse führt – nur die Kennung, mehr braucht die Regel nicht. */
export type Assigned = { readonly curriculumId: string };

/**
 * Zuordenbar ist, was nicht stillgelegt ist, eine veröffentlichte Fassung hat
 * und noch nicht an der Klasse liegt.
 *
 * Für eine neue Klasse ist `assigned` leer; dann steht der ganze Katalog zur
 * Wahl. Es braucht dafür keinen zweiten Fall.
 */
export function assignableTo(
  catalog: readonly CourseOption[],
  assigned: readonly Assigned[],
): CourseOption[] {
  const have = new Set(assigned.map((a) => a.curriculumId));
  return catalog
    .filter(
      (option) =>
        option.retiredAt === null &&
        option.latestVersionId !== null &&
        !have.has(option.curriculumId),
    )
    .sort((a, b) => a.position - b.position || a.title.localeCompare(b.title, "de"));
}

/**
 * Warum gerade nichts zuordenbar ist – als Satz, nicht als leere Liste.
 *
 * Eine leere Auswahl ohne Begründung ist die Stelle, an der jemand anfängt, den
 * Knopf zu suchen, den es nicht gibt.
 */
export function whyNothingAssignable(
  catalog: readonly CourseOption[],
  assigned: readonly Assigned[],
): string {
  if (catalog.length === 0) return "Es sind noch keine Lehrgänge veröffentlicht.";
  const have = new Set(assigned.map((a) => a.curriculumId));
  const rest = catalog.filter((o) => !have.has(o.curriculumId));
  if (rest.length === 0) return "Diese Klasse führt bereits alle Lehrgänge.";
  return "Die übrigen Lehrgänge sind stillgelegt oder noch nicht veröffentlicht.";
}

/**
 * Beschriftung eines Lehrgangs in einer Auswahlliste.
 *
 * Nennt den Vorgänger, wenn es einen gibt und er im Katalog steht. Erzwungen
 * wird er nicht – aber „Grundkurs 2“ allein sagt der Lehrkraft nicht, dass die
 * Kinder dafür Grundkurs 1 hinter sich haben sollten. Die Reihenfolge ist eine
 * inhaltliche Aussage; sie gehört an die Stelle, an der gewählt wird.
 *
 * Steht der Vorgänger nicht im Katalog – stillgelegt, nie veröffentlicht –,
 * bleibt der Zusatz weg: ein Name, den die Lehrkraft nirgends findet, hilft ihr
 * nicht.
 */
export function courseOptionLabel(
  option: CourseOption,
  catalog: readonly CourseOption[],
): string {
  const name = option.subtitle ? `${option.title} · ${option.subtitle}` : option.title;
  if (option.requiresId === null) return name;
  const vorgaenger = catalog.find((o) => o.curriculumId === option.requiresId);
  return vorgaenger ? `${name} (baut auf ${vorgaenger.title} auf)` : name;
}
